// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environment';

/**
 * DTO for an artifact from the API
 */
export interface ArtifactDto {
  id: string;
  name: string;
  type: string;
  url: string;
  model: string;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Response for the artifacts endpoint
 */
export interface ArtifactsResponse {
  artifacts: ArtifactDto[];
  total: number;
}

/**
 * Pagination parameters for artifacts requests
 */
export interface ArtifactsPaginationParams {
  offset: number;
  pageSize: number;
}

/**
 * ArtifactsRestService
 *
 * Frontend service that calls the REST endpoint for project artifacts.
 * Artifacts are project resources like presentations, documents,
 * websites, and repositories.
 * This service is responsible only for HTTP communication.
 */
@Injectable({
  providedIn: 'root',
})
export class ArtifactsRestService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}`;

  /**
   * Get paginated artifacts for a project.
   *
   * @param projectId - The Salesforce project ID
   * @param params - Pagination parameters (offset, pageSize)
   * @returns Observable of paginated artifacts response
   */
  getArtifacts(
    projectId: string,
    params: ArtifactsPaginationParams
  ): Observable<ArtifactsResponse> {
    return this.http.get<ArtifactsResponse>(
      `${this.baseUrl}/projects/${projectId}/artifacts`,
      { params: { offset: params.offset, pageSize: params.pageSize } }
    );
  }
}
